/* CBH — Our Brands carousel controller
   - Prev/next buttons jump by configured "columns"
   - Optional progress bar
   - Optional autoplay + loop
   - Drag-to-scroll on desktop (prevents accidental clicks)
   - Keyboard support when focused (←/→)
   - Respects prefers-reduced-motion
*/

(function () {
  const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Reduce layout thrash on mobile: initialise only when near-viewport.
  const inited = new WeakSet();

  function whenVisible(el, cb) {
    if (!('IntersectionObserver' in window)) {
      cb();
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            io.disconnect();
            cb();
            return;
          }
        }
      },
      { rootMargin: '200px 0px' }
    );
    io.observe(el);
  }

  function colsFor(sectionEl) {
    const m = parseInt(sectionEl.getAttribute('data-cols-mobile') || '2', 10);
    const t = parseInt(sectionEl.getAttribute('data-cols-tablet') || '4', 10);
    const d = parseInt(sectionEl.getAttribute('data-cols-desktop') || '6', 10);

    if (window.matchMedia('(min-width: 1280px)').matches) return d;
    if (window.matchMedia('(min-width: 768px)').matches) return t;
    return m;
  }

  function init(sectionEl) {
    if (inited.has(sectionEl)) return;
    inited.add(sectionEl);

    const track = sectionEl.querySelector('[data-cbh-ob-track]');
    if (!track) return;

    const slides = Array.from(sectionEl.querySelectorAll('.cbh-our-brands__slide'));
    if (slides.length <= 1) {
      sectionEl.classList.add('is-static');
      return;
    }

    const prev = sectionEl.querySelector('[data-cbh-ob-prev]');
    const next = sectionEl.querySelector('[data-cbh-ob-next]');
    const bar = sectionEl.querySelector('[data-cbh-ob-bar]');

    const wantBar = sectionEl.getAttribute('data-scrollbar') === '1';
    const wantAutoplay = sectionEl.getAttribute('data-autoplay') === '1' && !reduceMotion;
    const wantLoop = sectionEl.getAttribute('data-loop') === '1';
    const interval = Math.max(2500, parseInt(sectionEl.getAttribute('data-interval') || '4500', 10));

    // Cache measurements to avoid repeated scrollWidth/clientWidth reads (helps forced reflow).
    let _maxScroll = 0;
    let _clientWidth = 0;
    let _scrollWidth = 0;
    let _slideOffsets = [];

    function measure() {
      _clientWidth = track.clientWidth;
      _scrollWidth = track.scrollWidth;
      _maxScroll = Math.max(0, _scrollWidth - _clientWidth);
      _slideOffsets = slides.map((s) => s.offsetLeft);
    }

    function maxScroll() {
      return _maxScroll;
    }

    function closestIndex() {
      const x = track.scrollLeft;
      let best = 0;
      let bestDist = Infinity;
      for (let i = 0; i < slides.length; i++) {
        const dist = Math.abs((_slideOffsets[i] || 0) - x);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      }
      return best;
    }

    function goToIndex(i) {
      const idx = Math.max(0, Math.min(slides.length - 1, i));
      const slide = slides[idx];
      if (!slide) return;
      track.scrollTo({ left: _slideOffsets[idx] || slide.offsetLeft || 0, behavior: reduceMotion ? 'auto' : 'smooth' });
    }

    function scrollByCols(dir) {
      const cols = Math.max(1, colsFor(sectionEl));
      const i = closestIndex();
      const target = i + dir * cols;
      if (wantLoop && target >= slides.length) return goToIndex(0);
      if (wantLoop && target < 0) return goToIndex(slides.length - 1);
      goToIndex(target);
    }

    function updateNav() {
      if (!prev || !next) return;
      const max = maxScroll();

      const hasOverflow = max > 2;
      sectionEl.classList.toggle('is-static', !hasOverflow);

      if (wantLoop) {
        prev.disabled = false;
        next.disabled = false;
        prev.setAttribute('aria-disabled', 'false');
        next.setAttribute('aria-disabled', 'false');
        return;
      }

      const atStart = track.scrollLeft <= 2;
      const atEnd = track.scrollLeft >= max - 2;

      prev.disabled = atStart;
      next.disabled = atEnd;
      prev.setAttribute('aria-disabled', atStart ? 'true' : 'false');
      next.setAttribute('aria-disabled', atEnd ? 'true' : 'false');
    }

    function updateBar() {
      if (!wantBar || !bar) return;
      const max = maxScroll();
      if (max <= 2) {
        bar.style.width = '100%';
        bar.style.transform = 'translateX(0)';
        return;
      }

      const ratio = _clientWidth / Math.max(1, _scrollWidth);
      const widthPct = Math.max(12, Math.min(100, ratio * 100));
      const p = Math.min(1, Math.max(0, track.scrollLeft / max));
      const xPct = (100 - widthPct) * p;

      bar.style.width = `${widthPct}%`;
      bar.style.transform = `translateX(${xPct}%)`;
    }

    // Drag-to-scroll (desktop)
    (function enableDragScroll() {
      let isDown = false;
      let startX = 0;
      let startLeft = 0;
      let pointerId = null;
      let moved = false;
      const threshold = 6;

      function endDrag() {
        if (!isDown) return;
        isDown = false;
        pointerId = null;
        track.classList.remove('is-dragging');
      }

      track.addEventListener('pointerdown', (e) => {
        if (e.button !== 0) return;
        if (e.pointerType === 'touch') return;

        isDown = true;
        moved = false;
        pointerId = e.pointerId;
        startX = e.clientX;
        startLeft = track.scrollLeft;
        track.setPointerCapture(pointerId);
      });

      track.addEventListener('pointermove', (e) => {
        if (!isDown || e.pointerId !== pointerId) return;
        const dx = e.clientX - startX;
        if (!moved && Math.abs(dx) < threshold) return;

        moved = true;
        track.classList.add('is-dragging');
        e.preventDefault();
        track.scrollLeft = startLeft - dx;
      });

      track.addEventListener('pointerup', endDrag);
      track.addEventListener('pointercancel', endDrag);
      track.addEventListener('lostpointercapture', endDrag);

      track.addEventListener('dragstart', (e) => e.preventDefault());

      // Prevent clicks after drag (stops accidental navigation)
      track.addEventListener(
        'click',
        (e) => {
          if (!moved) return;
          e.preventDefault();
          e.stopPropagation();
          moved = false;
        },
        true
      );
    })();

    // Controls
    prev && prev.addEventListener('click', () => scrollByCols(-1));
    next && next.addEventListener('click', () => scrollByCols(1));

    // Keyboard support (when focused)
    track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        scrollByCols(-1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        scrollByCols(1);
      }
    });

    // Sync on scroll
    let raf = null;
    track.addEventListener(
      'scroll',
      () => {
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          updateNav();
          updateBar();
        });
      },
      { passive: true }
    );

    // Autoplay
    let autoplayTimer = null;
    function startAutoplay() {
      if (!wantAutoplay) return;
      stopAutoplay();
      autoplayTimer = window.setInterval(() => {
        const max = maxScroll();
        const atEnd = track.scrollLeft >= max - 2;
        if (atEnd) {
          if (wantLoop) {
            track.scrollTo({ left: 0, behavior: 'auto' });
          } else {
            stopAutoplay();
          }
        } else {
          scrollByCols(1);
        }
      }, interval);
    }

    function stopAutoplay() {
      if (autoplayTimer) window.clearInterval(autoplayTimer);
      autoplayTimer = null;
    }

    // Pause autoplay on interaction
    ['pointerdown', 'touchstart', 'keydown', 'mouseenter', 'focusin', 'wheel'].forEach((evt) => {
      track.addEventListener(evt, stopAutoplay, { passive: true });
    });
    sectionEl.addEventListener('mouseleave', startAutoplay);
    sectionEl.addEventListener('focusout', startAutoplay);

    // Re-sync on resize (rAF debounced)
    let resizeRaf = null;
    window.addEventListener(
      'resize',
      () => {
        if (resizeRaf) cancelAnimationFrame(resizeRaf);
        resizeRaf = requestAnimationFrame(() => {
          measure();
          updateNav();
          updateBar();
        });
      },
      { passive: true }
    );

    function initialSync() {
      measure();
      updateNav();
      updateBar();
    }

    // Run sync a few times to catch late layout changes
    initialSync();
    requestAnimationFrame(initialSync);
    window.setTimeout(initialSync, 60);
    window.setTimeout(initialSync, 250);
    window.addEventListener('load', initialSync, { once: true });
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(initialSync).catch(() => {});
    }

    startAutoplay();
  }

  function boot() {
    document.querySelectorAll('[data-cbh-our-brands]').forEach((el) => whenVisible(el, () => init(el)));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
